import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'
import router from '@/router'

// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/dev-api', // url = base url + request url
  // withCredentials: true, // 跨域请求时发送 cookies
  timeout: 5000 // 请求超时时间
})

let isRedirectingToLogin = false

function redirectToLogin(message = '登录已失效，请重新登录') {
  if (isRedirectingToLogin) return
  isRedirectingToLogin = true
  Message({
    message,
    type: 'error',
    duration: 3 * 1000
  })
  store.dispatch('user/resetToken').finally(() => {
    const redirect = router.currentRoute && router.currentRoute.fullPath !== '/login'
      ? router.currentRoute.fullPath
      : '/'
    router.replace(`/login?redirect=${encodeURIComponent(redirect)}`).finally(() => {
      isRedirectingToLogin = false
    })
  })
}

function isAuthErrorCode(code) {
  return [401, 50008, 50012, 50014].includes(Number(code))
}

function getErrorMessage(error) {
  const response = error && error.response
  const data = response && response.data
  const rawMessage = [
    typeof data === 'string' ? data : '',
    error && typeof error.message === 'string' ? error.message : ''
  ].join(' ')

  if (/Could not proxy request|ECONNREFUSED/i.test(rawMessage)) {
    return '无法连接本地服务，请确认后端服务已启动'
  }

  if (typeof data === 'string' && data.trim()) {
    return data.trim()
  }

  const message = data && (
    data.message ||
    data.msg ||
    data.error ||
    (data.errors && data.errors[0] && (data.errors[0].message || data.errors[0].msg))
  )

  if (typeof message === 'string' && message.trim()) {
    return message.trim()
  }

  if (response && response.status === 400) return '请求参数有误'
  if (response && response.status === 401) return '登录已失效，请重新登录'
  if (response && response.status === 403) return '暂无权限访问该内容'
  if (response && response.status === 404) return '请求的接口不存在'
  if (response && response.status >= 500) return '服务异常，请稍后重试'
  if (error && error.code === 'ECONNABORTED') return '请求超时，请稍后重试'
  if (error && error.code === 'ECONNREFUSED') return '无法连接本地服务，请确认后端服务已启动'
  if (error && error.message === 'Network Error') return '网络异常，请检查服务是否可用'

  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message.trim()
  }

  return '请求失败，请稍后重试'
}

// request 拦截器
service.interceptors.request.use(
  config => {
    if (store.getters.token) {
      // 让每个请求携带 token
      // ['Authorization'] 是自定义 headers key
      // 请根据实际情况修改
      config.headers['Authorization'] = 'Bearer ' + store.getters.token
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// response 拦截器
service.interceptors.response.use(
  /**
   * 如果你想要获取 http 信息诸如 headers 或 status
   * 请 return  response => response
  */

  /**
   * 通过自定义 code 来判断请求状态
   * 这里仅仅是一个例子
   * 你也可以通过 HTTP Status Code 来判断
   */
  response => {
    if (response && ['blob', 'arraybuffer'].includes(response.config && response.config.responseType)) {
      return response
    }

    const res = response.data

    // 如果自定义 code 不是 0，则判断为错误
    if (res.code !== 0) {
      if (isAuthErrorCode(res.code)) {
        redirectToLogin(res.message || '登录已失效，请重新登录')
        const authError = new Error(res.message || 'Unauthorized')
        authError.__handled = true
        return Promise.reject(authError)
      }
      Message({
        message: res.message || 'Error',
        type: 'error',
        duration: 5 * 1000
      })
      const businessError = new Error(res.message || 'Error')
      businessError.__handled = true
      return Promise.reject(businessError)
    } else {
      return res
    }
  },
  error => {
    const status = error.response && error.response.status
    const message = getErrorMessage(error)
    if (isAuthErrorCode(status)) {
      redirectToLogin(message || '登录已失效，请重新登录')
      error.__handled = true
      return Promise.reject(error)
    }
    Message({
      message,
      type: 'error',
      duration: 5 * 1000
    })
    error.__handled = true
    return Promise.reject(error)
  }
)

export default service
